import * as React from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router';
import { QueryClientProvider, useMutation } from '@tanstack/react-query';
import { queryClient } from 'common/react-query/query-client';
import { getAppUrl, getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { useDashboardSidenavContext } from 'common/widgets/dashboard-sidenav/context';
import { useRequiredCanvasSidenavContext } from 'common/widgets/canvas-sidenav/context';
import { SidenavSectionIndex, SidenavSectionTrackingName } from 'common/widgets/canvas-sidenav/constants';
import { getCommonCanvasTrackingProps, TrackingEvent } from 'common/report/canvas/tracking';
import { CanvasView } from './canvas-view';
import { useCanvasApi } from './api/canvas';
import styles from './index.module.styl';
function onError(error: Error) {
  addToast({
    message: error.message,
    type: ToastType.ERROR
  });
  throw error;
}
type ViewProps = {
  projectId: number;
  workspaceId: number;
  canvasId: number;
};
const View: React.FC<ViewProps> = props => {
  var _userCookieUtils$getU, _userCookieUtils$getU2;
  const userId = (_userCookieUtils$getU = (_userCookieUtils$getU2 = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU2 === void 0 ? void 0 : _userCookieUtils$getU2.id) !== null && _userCookieUtils$getU !== void 0 ? _userCookieUtils$getU : 0;
  return <CanvasView projectId={props.projectId} workspaceId={props.workspaceId} userId={userId} canvasId={props.canvasId} onError={onError} data-sentry-element="CanvasView" data-sentry-component="View" data-sentry-source-file="index.tsx" />;
};
type CreateProps = {
  projectId: number;
  workspaceId: number;
};
const Create: React.FC<CreateProps> = _ref => {
  let {
    projectId,
    workspaceId
  } = _ref;
  const navigate = useNavigate();
  const canvasSidenavContext = useRequiredCanvasSidenavContext();
  const created = React.useRef(false);
  const {
    createCanvas
  } = useCanvasApi({
    projectId,
    workspaceId
  });
  const mutation = useMutation({
    mutationFn: createCanvas,
    onSuccess: response => {
      canvasSidenavContext.insertCanvasSidenavItem(response, SidenavSectionIndex.Yours, SidenavSectionTrackingName.Yours);
      mixpanel.track(TrackingEvent.CreateCanvas, getCommonCanvasTrackingProps({
        canvas: response
      }));
      navigate(getReportUrl("canvas/".concat(response.id), {
        projectId,
        workspaceId
      }));
    },
    onError: error => {
      addToast({
        message: error.message,
        type: ToastType.ERROR
      });
      navigate(getAppUrl("home"));
    }
  });
  React.useEffect(() => {
    if (!created.current) {
      created.current = true;
      mutation.mutate({
        name: "Untitled"
      });
    }
  }, []); // run only once on mount

  return <div className={styles.appContainer} data-sentry-component="Create" data-sentry-source-file="index.tsx">
      <SpinnerReact size={SpinnerSize.Large} data-sentry-element="SpinnerReact" data-sentry-source-file="index.tsx" />
    </div>;
};
type BaseProps = {
  projectId: number;
  workspaceId: number;
};
const Base: React.FC<BaseProps> = _ref2 => {
  let {
    projectId,
    workspaceId
  } = _ref2;
  const {
    canvasId
  } = useParams<{
    canvasId: string;
  }>();
  const sidenavContext = useDashboardSidenavContext();
  React.useEffect(() => {
    sidenavContext === null || sidenavContext === void 0 || sidenavContext.setSidenavState({
      showSidenav: true,
      selectedNavItem: canvasId ? "c_".concat(canvasId) : ""
    });
  }, [canvasId]);
  return <View projectId={projectId} workspaceId={workspaceId} canvasId={parseInt(canvasId!, 10)} data-sentry-element="View" data-sentry-component="Base" data-sentry-source-file="index.tsx" />;
};
function CanvasApp() {
  const projectId = parseInt(getProjectId(), 10);
  const workspaceId = parseInt(getWorkspaceId(), 10);
  return <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-component="CanvasApp" data-sentry-source-file="index.tsx">
      <Routes data-sentry-element="Routes" data-sentry-source-file="index.tsx">
        <Route path='' element={<Create projectId={projectId} workspaceId={workspaceId} />} data-sentry-element="Route" data-sentry-source-file="index.tsx" />
        <Route path=':canvasId' element={<Base projectId={projectId} workspaceId={workspaceId} />} data-sentry-element="Route" data-sentry-source-file="index.tsx" />
      </Routes>
    </QueryClientProvider>;
}
CanvasApp.displayName = "CanvasApp";

// for lazy loading route
// eslint-disable-next-line import/no-default-export
export default CanvasApp;